"use client";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from "react";
import { PixelText } from "@/components/ui/pixel-text";
import { audio as audioEngine, type SfxKey } from "@/engine/audio";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type TextSpeed = "slow" | "normal" | "fast" | "instant";
export type BattleAnimations = "full" | "reduced" | "off";
export type Daltonism =
  | "none"
  | "protanopia"
  | "deuteranopia"
  | "tritanopia";

export type Settings = {
  masterVolume: number; // 0..1
  sfxVolume: number;
  bgmVolume: number;
  textSpeed: TextSpeed;
  battleAnimations: BattleAnimations;
  cameraShake: boolean;
  daltonism: Daltonism;
  showControlHints: boolean;
};

export const DEFAULT_SETTINGS: Settings = {
  masterVolume: 0.7,
  sfxVolume: 0.8,
  bgmVolume: 0.5,
  textSpeed: "normal",
  battleAnimations: "full",
  cameraShake: true,
  daltonism: "none",
  showControlHints: true,
};

type Props = {
  open: boolean;
  settings: Settings;
  onChange: (next: Settings) => void;
  onClose: () => void;
};

// ---------------------------------------------------------------------------
// Storage helpers (SSR-safe)
// ---------------------------------------------------------------------------

const STORAGE_KEY = "surggies:settings";

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function clamp01(v: unknown): number {
  if (typeof v !== "number" || !Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function asTextSpeed(v: unknown): TextSpeed | null {
  return v === "slow" || v === "normal" || v === "fast" || v === "instant"
    ? v
    : null;
}

function asBattleAnimations(v: unknown): BattleAnimations | null {
  return v === "full" || v === "reduced" || v === "off" ? v : null;
}

function asDaltonism(v: unknown): Daltonism | null {
  return v === "none" ||
    v === "protanopia" ||
    v === "deuteranopia" ||
    v === "tritanopia"
    ? v
    : null;
}

export function loadSettings(): Settings {
  if (!isBrowser()) return { ...DEFAULT_SETTINGS };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return { ...DEFAULT_SETTINGS };
    }
    const o = parsed as Record<string, unknown>;
    const next: Settings = { ...DEFAULT_SETTINGS };
    if (typeof o.masterVolume === "number") {
      next.masterVolume = clamp01(o.masterVolume);
    }
    if (typeof o.sfxVolume === "number") {
      next.sfxVolume = clamp01(o.sfxVolume);
    }
    if (typeof o.bgmVolume === "number") {
      next.bgmVolume = clamp01(o.bgmVolume);
    }
    const ts = asTextSpeed(o.textSpeed);
    if (ts) next.textSpeed = ts;
    const ba = asBattleAnimations(o.battleAnimations);
    if (ba) next.battleAnimations = ba;
    if (typeof o.cameraShake === "boolean") next.cameraShake = o.cameraShake;
    const dl = asDaltonism(o.daltonism);
    if (dl) next.daltonism = dl;
    if (typeof o.showControlHints === "boolean") {
      next.showControlHints = o.showControlHints;
    }
    return next;
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(s: Settings): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // Ignore — storage may be unavailable (private mode, quota, etc.).
  }
}

// ---------------------------------------------------------------------------
// Audio plumbing
// ---------------------------------------------------------------------------

type SurggiesAudio = {
  playSfx?: (key: SfxKey) => void;
  setVolumes?: (opts: {
    master?: number;
    sfx?: number;
    bgm?: number;
  }) => void;
};

function getAudio(): SurggiesAudio | undefined {
  if (!isBrowser()) return undefined;
  // Prefer the imported singleton (always defined, even pre-init).
  // Fall back to window.surggiesAudio in case the engine is provided externally.
  const fromWindow = (window as unknown as { surggiesAudio?: SurggiesAudio })
    .surggiesAudio;
  return audioEngine ?? fromWindow;
}

function playSfx(name: SfxKey): void {
  getAudio()?.playSfx?.(name);
}

// ---------------------------------------------------------------------------
// Palette (PIXEL_STYLE.md)
// ---------------------------------------------------------------------------

const CREAM = "#f5ecd6";
const INK = "#1a1a1a";
const WALNUT = "#5c4a30";
const SAND = "#b89968";
const BRONZE = "#8a7a5c";

// ---------------------------------------------------------------------------
// Row definitions
// ---------------------------------------------------------------------------

type RowId =
  | "master"
  | "sfx"
  | "bgm"
  | "textSpeed"
  | "battleAnimations"
  | "cameraShake"
  | "daltonism"
  | "showControlHints"
  | "reset"
  | "apply";

const ROWS: { id: RowId; label: string }[] = [
  { id: "master", label: "Master Volume" },
  { id: "sfx", label: "SFX Volume" },
  { id: "bgm", label: "BGM Volume" },
  { id: "textSpeed", label: "Text Speed" },
  { id: "battleAnimations", label: "Battle Animations" },
  { id: "cameraShake", label: "Camera Shake" },
  { id: "daltonism", label: "Color Vision Mode" },
  { id: "showControlHints", label: "Show Control Hints" },
  { id: "reset", label: "Reset to Defaults" },
  { id: "apply", label: "Apply" },
];

const TEXT_SPEEDS: TextSpeed[] = ["slow", "normal", "fast", "instant"];
const BATTLE_ANIMS: BattleAnimations[] = ["full", "reduced", "off"];
const DALTONISMS: Daltonism[] = [
  "none",
  "protanopia",
  "deuteranopia",
  "tritanopia",
];

function labelize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function cycle<T>(arr: readonly T[], current: T, delta: number): T {
  const i = arr.indexOf(current);
  const base = i < 0 ? 0 : i;
  const next = (base + delta + arr.length) % arr.length;
  return arr[next];
}

function stepVolume(v: number, delta: number): number {
  // 0..1 in 10% steps (0.1). Round to nearest tenth so floating values from
  // localStorage still snap to the bar cleanly.
  const stepped = Math.round(v * 10) / 10 + delta * 0.1;
  return clamp01(Math.round(stepped * 10) / 10);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SettingsMenu({
  open,
  settings,
  onChange,
  onClose,
}: Props): ReactElement | null {
  const [cursor, setCursor] = useState(0);
  const prevOpen = useRef(false);

  // On mount: load persisted settings and apply volumes to the audio engine
  // so volume preferences survive across sessions even before the menu opens.
  useEffect(() => {
    const persisted = loadSettings();
    getAudio()?.setVolumes?.({
      master: persisted.masterVolume,
      sfx: persisted.sfxVolume,
      bgm: persisted.bgmVolume,
    });
    // Mount-only — parent owns the Settings state, so we don't push it back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset cursor + fire open/close sfx on transitions.
  useEffect(() => {
    if (open && !prevOpen.current) {
      playSfx("menu_open");
      setCursor(0);
    } else if (!open && prevOpen.current) {
      playSfx("menu_close");
    }
    prevOpen.current = open;
  }, [open]);

  const commit = useCallback(
    (next: Settings, opts?: { volumeChanged?: boolean }) => {
      onChange(next);
      saveSettings(next);
      if (opts?.volumeChanged) {
        getAudio()?.setVolumes?.({
          master: next.masterVolume,
          sfx: next.sfxVolume,
          bgm: next.bgmVolume,
        });
      }
    },
    [onChange],
  );

  const adjustRow = useCallback(
    (rowId: RowId, delta: number): boolean => {
      // Returns true if a setting actually changed.
      switch (rowId) {
        case "master": {
          const v = stepVolume(settings.masterVolume, delta);
          if (v === settings.masterVolume) return false;
          commit({ ...settings, masterVolume: v }, { volumeChanged: true });
          return true;
        }
        case "sfx": {
          const v = stepVolume(settings.sfxVolume, delta);
          if (v === settings.sfxVolume) return false;
          commit({ ...settings, sfxVolume: v }, { volumeChanged: true });
          return true;
        }
        case "bgm": {
          const v = stepVolume(settings.bgmVolume, delta);
          if (v === settings.bgmVolume) return false;
          commit({ ...settings, bgmVolume: v }, { volumeChanged: true });
          return true;
        }
        case "textSpeed": {
          const v = cycle(TEXT_SPEEDS, settings.textSpeed, delta);
          if (v === settings.textSpeed) return false;
          commit({ ...settings, textSpeed: v });
          return true;
        }
        case "battleAnimations": {
          const v = cycle(BATTLE_ANIMS, settings.battleAnimations, delta);
          if (v === settings.battleAnimations) return false;
          commit({ ...settings, battleAnimations: v });
          return true;
        }
        case "cameraShake": {
          // Either arrow flips boolean.
          const v = !settings.cameraShake;
          commit({ ...settings, cameraShake: v });
          return true;
        }
        case "daltonism": {
          const v = cycle(DALTONISMS, settings.daltonism, delta);
          if (v === settings.daltonism) return false;
          commit({ ...settings, daltonism: v });
          return true;
        }
        case "showControlHints": {
          const v = !settings.showControlHints;
          commit({ ...settings, showControlHints: v });
          return true;
        }
        case "reset":
        case "apply":
          return false;
      }
    },
    [settings, commit],
  );

  const confirmRow = useCallback(
    (rowId: RowId) => {
      switch (rowId) {
        case "cameraShake":
        case "showControlHints":
          if (adjustRow(rowId, 0)) playSfx("menu_select");
          return;
        case "textSpeed":
        case "battleAnimations":
        case "daltonism":
          if (adjustRow(rowId, 1)) playSfx("menu_select");
          return;
        case "master":
        case "sfx":
        case "bgm":
          // Z on a slider is a no-op confirm — sliders adjust via ←→.
          playSfx("menu_select");
          return;
        case "reset": {
          playSfx("menu_select");
          commit({ ...DEFAULT_SETTINGS }, { volumeChanged: true });
          return;
        }
        case "apply": {
          playSfx("menu_select");
          onClose();
          return;
        }
      }
    },
    [adjustRow, commit, onClose],
  );

  const moveCursor = useCallback((delta: number) => {
    setCursor((c) => (c + delta + ROWS.length) % ROWS.length);
    playSfx("menu_move");
  }, []);

  // Keyboard handling.
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      if (
        key === "ArrowUp" ||
        key === "ArrowDown" ||
        key === "ArrowLeft" ||
        key === "ArrowRight" ||
        key === "Enter" ||
        key === "z" ||
        key === "Z" ||
        key === "x" ||
        key === "X" ||
        key === "Escape"
      ) {
        e.preventDefault();
      }

      const row = ROWS[cursor];

      switch (key) {
        case "ArrowUp":
          moveCursor(-1);
          break;
        case "ArrowDown":
          moveCursor(1);
          break;
        case "ArrowLeft":
          if (adjustRow(row.id, -1)) playSfx("menu_select");
          break;
        case "ArrowRight":
          if (adjustRow(row.id, 1)) playSfx("menu_select");
          break;
        case "Enter":
        case "z":
        case "Z":
          confirmRow(row.id);
          break;
        case "x":
        case "X":
        case "Escape":
          onClose();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, cursor, adjustRow, confirmRow, moveCursor, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Settings menu"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(26, 26, 26, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 60,
        imageRendering: "pixelated",
      }}
    >
      <div
        style={{
          width: 480,
          maxHeight: "92vh",
          overflowY: "auto",
          backgroundColor: CREAM,
          border: `2px solid ${INK}`,
          boxShadow: `inset 1px 1px 0 ${CREAM}, inset -1px -1px 0 ${WALNUT}`,
          padding: "1rem 1.25rem",
        }}
      >
        <PixelText
          as="div"
          glow="none"
          size="lg"
          className="!text-[#1a1a1a] !tracking-widest"
          style={{ marginBottom: "0.875rem" }}
        >
          Settings
        </PixelText>

        <ul
          role="menu"
          aria-label="Settings options"
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: "0.375rem",
          }}
        >
          {ROWS.map((row, i) => {
            const active = i === cursor;
            const isAction = row.id === "reset" || row.id === "apply";

            return (
              <li key={row.id} style={{ margin: 0 }}>
                <button
                  type="button"
                  role="menuitem"
                  onMouseEnter={() => {
                    if (cursor !== i) {
                      setCursor(i);
                      playSfx("menu_move");
                    }
                  }}
                  onClick={() => {
                    setCursor(i);
                    confirmRow(row.id);
                  }}
                  className="font-mono font-bold uppercase tracking-widest"
                  style={rowButtonStyle(active, isAction)}
                >
                  {active && (
                    <span
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: "0.5rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: CREAM,
                        fontSize: "0.9rem",
                        lineHeight: 1,
                      }}
                    >
                      &#9654;
                    </span>
                  )}

                  <span
                    style={{
                      fontSize: "0.85rem",
                      lineHeight: 1,
                    }}
                  >
                    {row.label}
                  </span>

                  {!isAction && (
                    <span
                      style={{
                        marginLeft: "auto",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <RowControl
                        row={row.id}
                        settings={settings}
                        active={active}
                      />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        <div
          aria-hidden
          className="font-mono uppercase tracking-widest"
          style={{
            marginTop: "0.875rem",
            paddingTop: "0.5rem",
            borderTop: `2px solid ${BRONZE}`,
            fontSize: "0.65rem",
            color: SAND,
            lineHeight: 1.3,
            display: "flex",
            justifyContent: "space-between",
            gap: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          <span>{"\u2191\u2193 Navigate"}</span>
          <span>{"\u2190\u2192 Adjust"}</span>
          <span>Z Confirm</span>
          <span>X Close</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row controls
// ---------------------------------------------------------------------------

function rowButtonStyle(active: boolean, isAction: boolean): CSSProperties {
  return {
    position: "relative",
    width: "100%",
    textAlign: "left",
    backgroundColor: active ? INK : CREAM,
    color: active ? CREAM : INK,
    border: `2px solid ${INK}`,
    padding: "0.5rem 0.75rem 0.5rem 1.75rem",
    lineHeight: 1,
    transition: "none",
    imageRendering: "pixelated",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: isAction ? "center" : "flex-start",
    gap: "0.75rem",
  };
}

function RowControl({
  row,
  settings,
  active,
}: {
  row: RowId;
  settings: Settings;
  active: boolean;
}): ReactElement | null {
  switch (row) {
    case "master":
      return <VolumeBar value={settings.masterVolume} active={active} />;
    case "sfx":
      return <VolumeBar value={settings.sfxVolume} active={active} />;
    case "bgm":
      return <VolumeBar value={settings.bgmVolume} active={active} />;
    case "textSpeed":
      return (
        <OptionToggle
          options={TEXT_SPEEDS}
          value={settings.textSpeed}
          active={active}
        />
      );
    case "battleAnimations":
      return (
        <OptionToggle
          options={BATTLE_ANIMS}
          value={settings.battleAnimations}
          active={active}
        />
      );
    case "cameraShake":
      return (
        <OnOff value={settings.cameraShake} active={active} />
      );
    case "daltonism":
      return (
        <OptionToggle
          options={DALTONISMS}
          value={settings.daltonism}
          active={active}
        />
      );
    case "showControlHints":
      return (
        <OnOff value={settings.showControlHints} active={active} />
      );
    case "reset":
    case "apply":
      return null;
  }
}

// ---------------------------------------------------------------------------
// VolumeBar — pixelated 10-segment bar
// ---------------------------------------------------------------------------

function VolumeBar({
  value,
  active,
}: {
  value: number;
  active: boolean;
}): ReactElement {
  const filled = Math.round(clamp01(value) * 10); // 0..10
  const segments = useMemo(() => {
    const arr: boolean[] = [];
    for (let i = 0; i < 10; i++) arr.push(i < filled);
    return arr;
  }, [filled]);

  const onColor = active ? CREAM : INK;
  const offColor = active ? WALNUT : SAND;
  const frameColor = active ? CREAM : INK;
  const pct = `${filled * 10}%`;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5rem",
      }}
    >
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          gap: 2,
          padding: 2,
          border: `2px solid ${frameColor}`,
          backgroundColor: active ? INK : CREAM,
          imageRendering: "pixelated",
        }}
      >
        {segments.map((on, i) => (
          <span
            key={i}
            style={{
              width: 8,
              height: 12,
              backgroundColor: on ? onColor : offColor,
              imageRendering: "pixelated",
            }}
          />
        ))}
      </span>
      <span
        style={{
          fontSize: "0.7rem",
          width: 36,
          textAlign: "right",
          lineHeight: 1,
        }}
      >
        {pct}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// OptionToggle — < Value > with arrow chevrons
// ---------------------------------------------------------------------------

function OptionToggle<T extends string>({
  options,
  value,
  active,
}: {
  options: readonly T[];
  value: T;
  active: boolean;
}): ReactElement {
  const idx = options.indexOf(value);
  const total = options.length;
  const display = labelize(value);
  const dimColor = active ? WALNUT : SAND;
  const fgColor = active ? CREAM : INK;

  const canLeft = idx > 0;
  const canRight = idx < total - 1;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.4rem",
        minWidth: 140,
        justifyContent: "flex-end",
      }}
    >
      <span
        aria-hidden
        style={{
          color: canLeft ? fgColor : dimColor,
          fontSize: "0.75rem",
          lineHeight: 1,
        }}
      >
        {"\u25C0"}
      </span>
      <span
        style={{
          fontSize: "0.75rem",
          lineHeight: 1,
          minWidth: 80,
          textAlign: "center",
        }}
      >
        {display}
      </span>
      <span
        aria-hidden
        style={{
          color: canRight ? fgColor : dimColor,
          fontSize: "0.75rem",
          lineHeight: 1,
        }}
      >
        {"\u25B6"}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// OnOff toggle
// ---------------------------------------------------------------------------

function OnOff({
  value,
  active,
}: {
  value: boolean;
  active: boolean;
}): ReactElement {
  const onBg = active ? CREAM : INK;
  const onFg = active ? INK : CREAM;
  const offBg = active ? INK : CREAM;
  const offFg = active ? CREAM : INK;
  const dim = active ? WALNUT : SAND;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      <span
        style={{
          padding: "2px 6px",
          fontSize: "0.7rem",
          lineHeight: 1,
          border: `2px solid ${value ? (active ? CREAM : INK) : dim}`,
          backgroundColor: value ? onBg : "transparent",
          color: value ? onFg : dim,
        }}
      >
        On
      </span>
      <span
        style={{
          padding: "2px 6px",
          fontSize: "0.7rem",
          lineHeight: 1,
          border: `2px solid ${!value ? (active ? CREAM : INK) : dim}`,
          backgroundColor: !value ? offBg : "transparent",
          color: !value ? offFg : dim,
        }}
      >
        Off
      </span>
    </span>
  );
}
